import React, { useState, useMemo } from 'react';
import { BarChart3, TrendingUp, Calendar, Filter, ChevronDown } from 'lucide-react';
import { getTranslation } from '../utils/translations';
import { getIconColor, getThemeColors, getFocusRingColor, getTextColor } from '../utils/themeUtils';
import type { Language, DailyPrayers, Theme } from '../types';

interface VisualizationProps {
  isDark: boolean;
  language: Language;
  prayerHistory: DailyPrayers[];
  theme: Theme;
}

type PeriodType = 'daily' | 'weekly' | 'monthly' | 'custom';
type ChartType = 'completion' | 'prayers' | 'status' | 'timing';

export function Visualization({ isDark, language, prayerHistory, theme }: VisualizationProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('weekly');
  const [selectedChart, setSelectedChart] = useState<ChartType>('completion');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  const themeColors = getThemeColors(theme);

  // Filter data based on selected period
  const filteredData = useMemo(() => {
    const now = new Date();
    let startDate: Date;
    let endDate = now;

    switch (selectedPeriod) {
      case 'daily':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7); // Last 7 days
        break;
      case 'weekly':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30); // Last 30 days
        break;
      case 'monthly':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3); // Last 3 months
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);
    }

    return prayerHistory.filter(day => {
      const dayDate = new Date(day.date);
      return dayDate >= startDate && dayDate <= endDate;
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [prayerHistory, selectedPeriod, customStartDate, customEndDate]);

  // Calculate completion rate data
  const completionData = useMemo(() => {
    return filteredData.map(day => ({
      date: day.date,
      completed: day.prayers.filter(p => p.completed).length,
      total: day.prayers.length,
      rate: (day.prayers.filter(p => p.completed).length / day.prayers.length) * 100
    }));
  }, [filteredData]);

  // Calculate prayer-specific data
  const prayerSpecificData = useMemo(() => {
    const prayerNames = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
    return prayerNames.map(prayerName => {
      const completed = filteredData.reduce((sum, day) => {
        const prayer = day.prayers.find(p => p.name === prayerName);
        return sum + (prayer?.completed ? 1 : 0);
      }, 0);
      const total = filteredData.length;
      return {
        name: prayerName,
        completed,
        total,
        rate: total > 0 ? (completed / total) * 100 : 0
      };
    });
  }, [filteredData]);

  // Calculate status data (jamaah vs individual)
  const statusData = useMemo(() => {
    const jamaah = filteredData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed && p.prayerStatus === 'jamaah').length;
    }, 0);
    const individual = filteredData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed && p.prayerStatus === 'individual').length;
    }, 0);
    const total = jamaah + individual;
    
    return [
      { name: 'Congregation', value: jamaah, percentage: total > 0 ? (jamaah / total) * 100 : 0 },
      { name: 'Individual', value: individual, percentage: total > 0 ? (individual / total) * 100 : 0 }
    ];
  }, [filteredData]);

  // Calculate timing data (on-time vs late vs qodo)
  const timingData = useMemo(() => {
    const onTime = filteredData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed && p.timeStatus === 'ontime').length;
    }, 0);
    const late = filteredData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed && p.timeStatus === 'late').length;
    }, 0);
    const qodo = filteredData.reduce((sum, day) => {
      return sum + day.prayers.filter(p => p.completed && p.timeStatus === 'qodo').length;
    }, 0);
    const total = onTime + late + qodo;
    
    return [
      { name: 'On Time', value: onTime, percentage: total > 0 ? (onTime / total) * 100 : 0 },
      { name: 'Late', value: late, percentage: total > 0 ? (late / total) * 100 : 0 },
      { name: 'Qodo', value: qodo, percentage: total > 0 ? (qodo / total) * 100 : 0 }
    ];
  }, [filteredData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getPrayerName = (prayerName: string) => {
    const prayerKey = `prayer.${prayerName.toLowerCase()}`;
    return getTranslation(prayerKey, language);
  };

  const renderCompletionChart = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Prayer Completion Rate Over Time</h3>
      <div className="h-64 flex items-end justify-between gap-1 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
        {completionData.map((day) => (
          <div key={day.date} className="flex flex-col items-center flex-1 max-w-8">
            <div
              className="w-full rounded-t transition-all hover:opacity-80 cursor-pointer"
              style={{ 
                height: `${Math.max(day.rate * 2, 4)}px`,
                backgroundColor: themeColors[500]
              }}
              title={`${formatDate(day.date)}: ${day.completed}/${day.total} (${day.rate.toFixed(1)}%)`}
            />
            <span className="text-xs mt-1 transform -rotate-45 origin-top-left">
              {formatDate(day.date)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPrayerChart = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Prayer Completion by Type</h3>
      <div className="space-y-3">
        {prayerSpecificData.map((prayer) => (
          <div key={prayer.name} className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-medium">{getPrayerName(prayer.name)}</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {prayer.completed}/{prayer.total} ({prayer.rate.toFixed(1)}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="h-3 rounded-full transition-all"
                style={{ 
                  width: `${prayer.rate}%`,
                  backgroundColor: themeColors[500]
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStatusChart = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Prayer Status Distribution</h3>
      <div className="flex items-center justify-center">
        <div className="relative w-48 h-48">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="8"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={themeColors[500]}
              strokeWidth="8"
              strokeDasharray={`${statusData[0].percentage * 2.51} 251`}
              strokeLinecap="round"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#3b82f6"
              strokeWidth="8"
              strokeDasharray={`${statusData[1].percentage * 2.51} 251`}
              strokeDashoffset={`-${statusData[0].percentage * 2.51}`}
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
      <div className="space-y-2">
        {statusData.map((item, index) => (
          <div key={item.name} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: index === 0 ? themeColors[500] : '#3b82f6' }}
              />
              <span>{item.name}</span>
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {item.value} ({item.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTimingChart = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Prayer Timing Analysis</h3>
      <div className="flex items-center justify-center">
        <div className="relative w-48 h-48">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="8"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={themeColors[500]}
              strokeWidth="8"
              strokeDasharray={`${timingData[0].percentage * 2.51} 251`}
              strokeLinecap="round"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="8"
              strokeDasharray={`${timingData[1].percentage * 2.51} 251`}
              strokeDashoffset={`-${timingData[0].percentage * 2.51}`}
              strokeLinecap="round"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="#ef4444"
              strokeWidth="8"
              strokeDasharray={`${timingData[2].percentage * 2.51} 251`}
              strokeDashoffset={`-${timingData[0].percentage * 2.51 + timingData[1].percentage * 2.51}`}
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
      <div className="space-y-2">
        {timingData.map((item, index) => (
          <div key={item.name} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ 
                  backgroundColor: index === 0 ? themeColors[500] : 
                                 index === 1 ? '#f59e0b' : '#ef4444'
                }}
              />
              <span>{item.name}</span>
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {item.value} ({item.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderChart = () => {
    switch (selectedChart) {
      case 'completion':
        return renderCompletionChart();
      case 'prayers':
        return renderPrayerChart();
      case 'status':
        return renderStatusChart();
      case 'timing':
        return renderTimingChart();
      default:
        return renderCompletionChart();
    }
  };

  if (prayerHistory.length === 0) {
    return (
      <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 mb-8`}>
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-5 h-5" style={getIconColor(theme)} />
          <h2 className="text-lg font-semibold">Prayer Data Visualization</h2>
        </div>
        <div className="text-center py-8">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No prayer data available yet. Start tracking your prayers to see visualizations!</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6 mb-6 sm:mb-8`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5" style={getIconColor(theme)} />
          <h2 className="text-lg sm:text-xl font-semibold">Prayer Data Visualization</h2>
        </div>
        
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
          } transition-colors`}
        >
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filters</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filter Panel */}
      {isFilterOpen && (
        <div className={`p-4 rounded-lg mb-6 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Period Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Time Period</label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value as PeriodType)}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-600 border-gray-500 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2`}
                style={getFocusRingColor(theme)}
              >
                <option value="daily">Last 7 Days</option>
                <option value="weekly">Last 30 Days</option>
                <option value="monthly">Last 3 Months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Chart Type Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Chart Type</label>
              <select
                value={selectedChart}
                onChange={(e) => setSelectedChart(e.target.value as ChartType)}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-600 border-gray-500 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2`}
                style={getFocusRingColor(theme)}
              >
                <option value="completion">Completion Rate</option>
                <option value="prayers">Prayer Types</option>
                <option value="status">Prayer Status</option>
                <option value="timing">Timing Analysis</option>
              </select>
            </div>

            {/* Custom Date Range */}
            {selectedPeriod === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-600 border-gray-500 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2`}
                    style={getFocusRingColor(theme)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-600 border-gray-500 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2`}
                    style={getFocusRingColor(theme)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Chart Display */}
      <div className="min-h-[300px]">
        {filteredData.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No data available for the selected period.</p>
          </div>
        ) : (
          renderChart()
        )}
      </div>

      {/* Summary Stats */}
      {filteredData.length > 0 && (
        <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h4 className="font-medium mb-3">Summary Statistics</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold" style={getTextColor(theme)}>
                {filteredData.reduce((sum, day) => sum + day.prayers.filter(p => p.completed).length, 0)}
              </div>
              <div className="text-sm text-gray-500">Total Prayers</div>
            </div>
            <div>
              <div className="text-2xl font-bold" style={getTextColor(theme)}>
                {filteredData.length}
              </div>
              <div className="text-sm text-gray-500">Days Tracked</div>
            </div>
            <div>
              <div className="text-2xl font-bold" style={getTextColor(theme)}>
                {((filteredData.reduce((sum, day) => sum + day.prayers.filter(p => p.completed).length, 0) / 
                   (filteredData.length * 5)) * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500">Average Rate</div>
            </div>
            <div>
              <div className="text-2xl font-bold" style={getTextColor(theme)}>
                {Math.max(...completionData.map(d => d.rate)).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500">Best Day</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}